@if (item) {
  @let permittedMenuItems =
    (actionMenuItems | applyMenuItemPermissions | async) ?? [];
  @let menuItems = (permittedMenuItems | translateMenuItems | async) ?? [];
  <button
    [attr.data-id]="'search-item-wrapper-' + item.id"
    [attr.aria-disabled]="isRowFocusable ? null : 'true'"
    [attr.tabindex]="isRowFocusable ? null : -1"
    class="h-20 w-full text-justify"
    [class.bd-search-item-wrapper-deleted]="item.deleted"
    type="button"
    (dblclick)="onRowDoubleClick($event)"
    (click)="onRowClick($event)"
    (keydown.enter)="onRowKeyDown($event)"
    *transloco="
      let t;
      scope: 'featureSearch';
      prefix: 'featureSearch.searchResults'
    "
  >
    <div
      [attr.data-id]="'search-item-' + item.id"
      class="bd-search-item"
      [ngClass]="{
        'bd-search-item-selected': item.selected,
        'bd-search-item-active': isActive,
        'bd-search-item-disabled': item.disabled,
        'bd-search-item-deleted': item.deleted,
        'bd-multi-select-mode': isSelectionMode,
      }"
      [pTooltip]="itemTooltipKey ? t(itemTooltipKey) : ''"
      tooltipPosition="top"
      tooltipStyleClass="bd-search-result-tooltip"
    >
      @if (!isSelectionMode || item.deleted) {
        <div class="bd-search-item-icon-wrapper">
          <span
            [attr.data-id]="'search-item-icon-' + item.id"
            class="bd-search-item-icon {{ item.icon }}"
            [class.bd-search-item-icon-deleted]="item.deleted"
          ></span>
        </div>
      }
      <p-checkbox
        [attr.data-id]="'search-item-checkbox-' + item.id"
        [ariaLabel]="
          t('itemCheckboxAriaLabel', {
            itemName: item.name,
          })
        "
        [value]="item"
        [ngModel]="selectedItems"
        (click)="onCheckboxClick($event, item.id)"
        (keydown.enter)="onCheckboxKeyDown($event, item.id)"
        (ngModelChange)="onSelectedItemsChange($event)"
        [disabled]="item.disabled || item.deleted"
        checkboxIcon="ui-check"
      ></p-checkbox>
      <div class="flex flex-col gap-1 flex-1 min-w-0">
        <div
          class="bd-search-item-detail bd-caption"
          [attr.data-id]="'search-item-type-' + item.id"
        >
          <div>{{ item.type }}</div>
        </div>
        <div class="flex items-baseline justify-start gap-4">
          <div
            [attr.data-id]="'search-item-name-' + item.id"
            class="bd-search-item-name bd-body-regular"
          >
            {{ item.name }}
          </div>
          <div
            [attr.data-id]="'search-item-source-' + item.id"
            class="bd-search-item-detail-chip"
          >
            <span
              [attr.data-id]="'search-item-source-label-' + item.id"
              class="bd-chip-label bd-caption"
              >{{ t('source') }}</span
            >
            <div class="bd-chip-content">
              <i
                [attr.data-id]="'item-source-icon-' + item.id"
                class="bd-chip-icon ui-repository"
              ></i>
              <div
                [attr.data-id]="'item-source-name-' + item.id"
                class="bd-chip-text bd-label-small-gray"
                [ngClass]="{ 'bd-disabled': item.disabled }"
              >
                {{
                  item.deleted
                    ? t('deleted')
                    : item.externalSource || t('unify')
                }}
              </div>
            </div>
          </div>
          @if (item.usedInViews?.[0]; as firstUsedInView) {
            <div
              [attr.data-id]="'search-item-used-in-view-' + item.id"
              class="bd-search-item-detail-chip flex-1"
            >
              <span
                [attr.data-id]="'search-item-used-in-label-' + item.id"
                class="bd-chip-label bd-caption"
                >{{ t('usedIn') }}</span
              >
              <a
                [pTooltip]="t('usedInViewTooltip')"
                tooltipPosition="top"
                [routerLink]="'/views/' + firstUsedInView.id"
                target="_blank"
                class="bd-chip-content bd-used-in-chip"
                (click)="$event.stopPropagation()"
                (keydown)="$event.stopPropagation()"
              >
                <i
                  [attr.data-id]="'view-link-icon-' + item.id"
                  class="bd-chip-icon ui-new-tab"
                ></i>
                <div
                  [attr.data-id]="'view-link-name-' + item.id"
                  class="bd-chip-text bd-label-small-gray"
                >
                  {{ firstUsedInView.name }}
                </div>
              </a>
              @if (item.usedInViews && item.usedInViews.length > 1) {
                <p-button
                  [pTooltip]="
                    item.disabled || item.deleted ? '' : t('moreViewsTooltip')
                  "
                  tooltipPosition="top"
                  styleClass="bd-search-item-more-views-tag"
                  outlined="false"
                  [disabled]="item.disabled || item.deleted"
                  (onClick)="onMoreViewsClick($event)"
                >
                  <span
                    [attr.data-id]="'more-views-' + item.id"
                    class="bd-label-small-gray"
                    >+{{ item.usedInViews.length - 1 }}</span
                  >
                </p-button>
              }
            </div>
          }
        </div>
      </div>
      <div class="flex items-center gap-1">
        <span
          [pTooltip]="actionRoleTooltip"
          tooltipPosition="top"
          tooltipStyleClass="bd-action-button-tooltip"
          [attr.aria-disabled]="actionDisabled ? 'true' : null"
          [attr.tabindex]="actionDisabled ? -1 : null"
          [tooltipDisabled]="!actionRoleTooltip"
        >
          <p-button
            class="bd-search-item-button bd-action-button"
            [attr.data-id]="'search-item-action-' + item.id"
            [text]="true"
            [styleClass]="
              actionDisabled
                ? 'bd-neutral w-full bd-disabled'
                : 'bd-neutral w-full'
            "
            [disabled]="actionDisabled"
            (onClick)="onActionButtonClick($event)"
            [label]="t('addTo')"
          >
          </p-button>
        </span>
        @if (menuItems.length > 0) {
          <p-button
            [attr.data-id]="'search-item-more-actions-' + item.id"
            [attr.aria-label]="t('moreActionsBtnInfo')"
            [text]="true"
            [pTooltip]="item.deleted ? '' : t('moreActionsBtnInfo')"
            tooltipPosition="top"
            icon="ui-ellipsis-vertical"
            styleClass="bd-neutral {{
              actionMenu.visible ? 'bd-selected' : ''
            }}"
            (onClick)="onMoreActionsClick($event, actionMenu)"
            [tooltipDisabled]="item.deleted"
            [disabled]="item.deleted || (item.disabled && isSelectionMode)"
          ></p-button>
          <p-tiered-menu
            #actionMenu
            styleClass="bd-action-menu bd-toolbar-menu bd-more-menu bd-small"
            appendTo="body"
            [popup]="true"
            [autoDisplay]="false"
            [model]="menuItems"
          >
            <ng-template
              pTemplate="item"
              let-menuItem
              let-hasSubmenu="hasSubmenu"
            >
              <bd-menu-item
                [item]="menuItem"
                [hasSubmenu]="hasSubmenu"
                (itemClick)="onMenuItemClick($event.event, menuItem)"
              />
            </ng-template>
          </p-tiered-menu>
        }
      </div>
    </div>
  </button>
}
